import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Mounts its children only when the placeholder is close to the viewport (or
 * after the browser has been idle for a while, so crawlers and users who never
 * scroll still get the content). Keeps the initial render cheap on mobile.
 */
const DeferOnVisible = ({
  children,
  minHeight = "40vh",
  idleDelay = 2500,
}: {
  children: ReactNode;
  minHeight?: string;
  idleDelay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    let timer: number | undefined;

    if (el && "IntersectionObserver" in window) {
      const obs = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setShow(true);
            obs.disconnect();
          }
        },
        { rootMargin: "600px 0px" },
      );
      obs.observe(el);
      timer = window.setTimeout(() => setShow(true), idleDelay);
      return () => {
        obs.disconnect();
        if (timer) clearTimeout(timer);
      };
    }

    setShow(true);
  }, [show, idleDelay]);

  if (show) return <>{children}</>;
  return <div ref={ref} style={{ minHeight }} aria-hidden="true" />;
};

export default DeferOnVisible;
